import leapToFreedomImg from '../../assets/leap-to-freedom.jpeg';
import tunnel57Img from '../../assets/tunnel-57.jpg';
import type { TranslationRecord } from '../types';

export const en: TranslationRecord = {
  localeName: 'English',
  locales: {
    en: 'English',
    bg: 'Bulgarian',
  },
  meta: {
    title: 'CheckPoint Charly | Jazz Restaurant in Sofia',
    description:
      'Experience CheckPoint Charly in downtown Sofia — contemporary Bulgarian cuisine, curated wines, and intimate live jazz evenings.',
    routes: {
      home: {
        title: 'Home | CheckPoint Charly',
        description:
          'Rediscover a beloved Sofia restaurant where seasonal Bulgarian dishes, curated wines, and live jazz Fridays create unforgettable evenings.',
      },
      about: {
        title: 'Our Story | CheckPoint Charly',
        description: 'Meet the team behind CheckPoint Charly and learn the story that inspires our hospitality, cuisine, and music.',
      },
      menu: {
        title: 'Menu | CheckPoint Charly',
        description: 'Explore our seasonal à la carte dishes, handcrafted cocktails, and curated wine list.',
      },
      lunchMenu: {
        title: 'Lunch Menu | CheckPoint Charly',
        description: 'See today’s lunch specials with freshly prepared dishes served every weekday in Sofia.',
      },
      gallery: {
        title: 'Gallery | CheckPoint Charly',
        description: 'Browse photos of our warm dining room, signature plates, courtyard garden, and memorable jazz nights.',
      },
      contact: {
        title: 'Contact | CheckPoint Charly',
        description: 'Contact the CheckPoint Charly team in Sofia for reservations, private dining inquiries, or special events.',
      },
      privacy: {
        title: 'Privacy Policy | CheckPoint Charly',
        description: 'Understand how CheckPoint Charly protects guest information collected through our website, forms, and marketing.',
      },
      terms: {
        title: 'Terms & Conditions | CheckPoint Charly',
        description: 'Review the terms that govern website use, reservations, and guest responsibilities at CheckPoint Charly.',
      },
    },
  },
  navigation: {
    home: 'Home',
    about: 'About',
    menu: 'Menu',
    lunchMenu: 'Lunch Menu',
    gallery: 'Gallery',
    contact: 'Contacts',
    privacy: 'Privacy Policy',
    terms: 'Terms & Conditions',
  },
  header: {
    cta: 'Get in touch',
    menuLabel: 'Menu',
    menuOpen: 'Open navigation',
    menuClose: 'Close navigation',
    aria: {
      themeToggle: 'Toggle theme',
      languageToggle: 'Switch language',
    },
  },
  footer: {
    restaurantName: 'CheckPoint Charly',
    addressLine1: 'Sofia Center, ul. "Ivan Vazov" 12',
    country: 'Bulgaria',
    viewOnMap: 'View on map',
    sendMessage: 'Send a message',
    menusHeading: 'Menus',
    policiesHeading: 'Policies & Legal',
    privacy: 'Privacy Policy',
    terms: 'Terms & Conditions',
    contact: 'check_charly@abv.bg',
    copyright: ' 2025 CheckPoint Charly. All rights reserved.',
    poweredBy: 'Designed & Powered by',
    followUs: 'Follow us',
    contactEmail: 'check_charly@abv.bg',
    emailLabel: 'Email',
    mobilePhoneLabel: 'Mobile',
    mobilePhoneNumber: '+359 886 577 342',
    landlinePhoneLabel: 'Stationary',
    landlinePhoneNumber: '+359 2 988 03 70',
    openHours: {
      heading: 'Open Hours',
      entries: [
        { day: 'Monday', hours: '11:30 AM - 11:30 PM' },
        { day: 'Tuesday', hours: '11:30 AM - 11:30 PM' },
        { day: 'Wednesday', hours: '11:30 AM - 11:30 PM' },
        { day: 'Thursday', hours: '11:30 AM - 11:30 PM' },
        { day: 'Friday', hours: '11:30 AM - 11:30 PM' },
        { day: 'Saturday', hours: '11:30 AM - 11:30 PM' },
        { day: 'Sunday', hours: '11:30 AM - 11:30 PM' },
      ],
    },
    newsletter: {
      heading: 'Stay in the loop',
      description: 'Receive seasonal menus, events, and special offers straight to your inbox.',
      placeholder: 'Your Email Address',
      cta: 'Sign up',
      success: 'Thank you! Please check your email for our next update.',
      error: 'Please enter a valid email address.',
      subject: 'New newsletter signup',
    },
    facebookAria: 'Visit CheckPoint Charly on Facebook',
    tripadvisorAria: 'Visit CheckPoint Charly on Tripadvisor',
  },
  home: {
    hero: {
      heading: 'Checkpoint Charly — history, great food, and jazz in the heart of Sofia',
      body:
        'Discover a cherished Sofia dining room where contemporary Bulgarian plates, curated wines, and welcoming hospitality take center stage—complete with intimate live jazz every Friday night.',
      menuCta: 'Explore the menu',
      bookingCta: 'Book a table',
    },
    booking: {
      heading: 'Reserve by phone',
      body:
        'Call our team to secure your table for lunch, dinner, or the Friday jazz session. We will confirm details right away.',
      callCta: 'Call +359 886 577 342',
    },
    concept: {
      heading: 'More than a restaurant',
      body:
        'Inspired by the famed Berlin Wall crossing, Checkpoint Charly serves vibrant, authentic cuisine alongside a history from an era that feels unimaginable today.',
      linkLabel: 'Read the full story',
    },
    reviews: {
      heading: 'Guests love the atmosphere',
      ratingLabel: 'Average Google rating',
      ratingValue: '4.8 / 5.0',
      footer: 'Real feedback gathered from Google Maps',
      list: [
        {
          name: 'Mina Koleva',
          text: 'Cozy vibe, jazz on point and the staff made sure our anniversary dinner felt special. The duck confit is a must.',
        },
        {
          name: 'Daniel Petrov',
          text: 'Weekly lunch spot for our team. Seasonal dishes, quick service, and they always suggest the perfect wine.',
        },
        {
          name: 'Alexandra Vassileva',
          text: 'Friday nights are magical – candlelight, live quartet, and desserts that disappear too fast. Highly recommend booking ahead.',
        },
      ],
    },
    mobileHighlights: [
      { name: 'React', logo: 'react' },
      { name: 'SEO', logo: 'seo' },
      { name: 'WordPress', logo: 'wordpress' },
      { name: 'Figma', logo: 'figma' },
    ],
    stats: [
      { label: 'Average go-live timeline', value: '10 days' },
      { label: 'Stakeholder touchpoints', value: '3 meetings' },
      { label: 'Managed launches per year', value: '120+' },
      { label: 'Payment providers ready', value: 'Stripe + more' },
    ],
    lighthouse: {
      badge: 'Google PageSpeed Insights Benchmark',
      heading: 'Perfect score on Google PageSpeed Insights',
      description:
        'Our optimized delivery processes consistently land a 100/100 in every PageSpeed Insights category, so your campaigns go live with elite performance, accessibility, security, and SEO baked in.',
      scores: [
        {
          label: 'Performance',
          value: '100',
          description: 'Instant load times on any device',
        },
        {
          label: 'Accessibility',
          value: '100',
          description: 'Inclusive experiences that welcome everyone',
        },
        {
          label: 'Best Practices',
          value: '100',
          description: 'Modern, secure implementation by default',
        },
        {
          label: 'SEO',
          value: '100',
          description: 'Technical foundations that help you rank',
        },
      ],
    },
  },
  about: {
    heading: 'The spirit behind CheckPoint Charly',
    intro:
      'Welcome to CheckPoint Charly — a place created for meaningful conversations, slow evenings, and the simple joys of being together. It is where friends linger over a meal, jazz floats through the room, and every visit feels personal.',
    paragraphs: [
      'Look closely at the vintage newspapers and photographs and you enter another era. The name takes us back to Checkpoint Charlie, the border crossing that separated East and West Berlin and changed the lives of everyone who made it to the other side.',
      'For two worlds divided by a single wall, crossing it was a dream. While the press in the “paradise” insisted the West was misery, reality was different — many lost their lives to border guards simply for longing for freedom. They risked everything to take those few steps toward a new life.',
      'To younger guests this may sound unimaginable. That is why the restaurant carries this name: not to dwell on sorrow, but to remember something precious — the right to choose where to live, to travel, and to gather without permission.',
      'Our name is a tribute to human courage. Two photographs in the dining room keep that memory alive better than any story. One shows a young East German soldier abandoning his post with a single leap that remade his future. The other, above the exit, captures an older man emerging from a tunnel dug by students. The moment he reaches daylight is the very essence of the unconquered human spirit.',
      'These images stand as a quiet reminder to value the freedom we enjoy today. They remind us to cherish every occasion to meet, laugh, and choose what to share at the table. When we treat those moments as a gift, they become true happiness. Welcome.',
    ],
    imageCaptions: {
      soldier: {
        heading: 'Leap to freedom',
        body: 'Conrad Schumann, the East German guard captured mid-jump on August 15, 1961, moments before landing in West Berlin.',
        alt: 'Historic photograph of Conrad Schumann leaping over barbed wire into West Berlin.',
        src: leapToFreedomImg,
      },
      tunnel: {
        heading: 'Arrival from the tunnel',
        body: 'A father emerging into the West after crawling through a student-dug tunnel — an enduring portrait of resilience.',
        alt: 'Historic photograph of an older man exiting a student-dug tunnel into West Berlin.',
        src: tunnel57Img,
      },
    },
  },
  menuPage: {
    heading: 'Main menu',
    body: 'Explore our full selection — salads, starters, mains, desserts, and signature drinks.',
    flipbookCta: 'Open flipbook',
    downloadCta: 'Download PDF',
    labels: {
      weight: 'Weight',
      allergens: 'Allergens',
      price: 'Price',
    },
    sections: {
      salads: {
        title: 'Salads',
        subtitle: 'Fresh greens, house specialties, and seafood signatures',
      },
      starters: {
        title: 'Starters',
        subtitle: 'Warm and cold bites, spreads, and seafood small plates',
      },
      mains: {
        title: 'Main courses',
        subtitle: 'Hearty dishes from the grill, the sea, and our chef’s specials',
      },
      desserts: {
        title: 'Desserts',
        subtitle: 'House-made sweets, classic favorites, and coffee pairings',
      },
      wine: {
        title: 'Wine List',
        subtitle: 'Curated selection of wines by the glass and bottle',
      },
      alcohol: {
        title: 'Alcohol',
        subtitle: 'Signature spirits, cocktails, and draught beers',
      },
      drinks: {
        title: 'Beverages',
        subtitle: 'Non-alcoholic refreshments and hot beverages',
      },
    },
    allergens: {
      heading: 'Allergen information',
      body: 'Allergen numbers follow EU Regulation No. 1169/2011. Please notify our team about any allergies before ordering.',
      legendLabel: 'Legend',
    },
  },
  gallery: {
    heading: 'Moments from the dining room',
    body: 'Get a taste of the ambiance—table settings, chef plates, and guest experiences.',
    close: 'Close gallery view',
    items: [
      {
        title: 'Courtyard evenings',
        description: 'Soft lights warm the garden for alfresco dinners.',
      },
      {
        title: 'Daytime garden table',
        description: 'Sunlit seating surrounded by greenery for relaxed lunches.',
      },
      {
        title: 'Dining room warmth',
        description: 'Dark wood and velvet chairs welcoming guests indoors.',
      },
      {
        title: 'Jazz corner',
        description: 'Instruments and stage lighting ready for nightly performances.',
      },
      {
        title: 'Gallery wall art',
        description: 'Framed pieces and candlelight create an intimate backdrop.',
      },
      {
        title: 'Signature decor',
        description: 'Vintage mirrors and textures add character to every seat.',
      },
    ],
  },
  plans: {
    title: 'Pick the partnership that fits your launch',
    intro:
      'Choose how you want to work with us — a one-time build, ongoing hosting, or the full partnership. Every option is written in plain language so your team can decide fast.',
    commitments: [
      {
        title: 'Every plan includes',
        bullets: [
          'Kickoff call to understand goals, audience, and tone',
          'Clean, on-brand layouts with accessible, mobile-ready pages',
          'Plain-language handover notes and reusable components',
        ],
      },
      {
        title: 'When we host for you',
        bullets: [
          'Global network tuned for speed and near-instant load times',
          'CDN, smart caching, and automated updates included',
          'Active monitoring with 100% uptime targets',
          'Benchmark-ready scores on Lighthouse and PageSpeed',
        ],
      },
    ],
    plans: [
      {
        name: 'Just Website Design',
        description: 'We craft your site from start to finish, then hand over the code so you can host it wherever you like.',
        price: 'One-time project fee',
        assurances: [
          'Rich page titles and descriptions ready for SEO',
          'Lightning-fast load times tuned before handoff',
          'Polished contact forms that go straight to your inbox',
          'Stripe-ready checkout and payment flows',
          'Newsletter signup blocks connected to your email tool',
          'Multi-language structure prepared for easy expansion',
          'White and dark mode variants for every key page',
          '14-day post-launch bug fix window',
        ],
        features: [
          'Brand workshop to define structure and messaging',
          'Responsive pages designed for leads and signups',
          'Step-by-step handover pack with code docs and checklists',
        ],
        cta: 'Start your project',
      },
      {
        name: 'End-to-End Partnership',
        description: 'We design, build, launch, and host your site, then stay on to refresh content, campaigns, and performance.',
        price: 'One-time build + monthly care',
        badge: 'Best performance',
        assurances: [
          {
            text: 'Everything from Just Website Design Plan included',
            highlight: 'Just Website Design Plan',
          },
          {
            text: 'Everything from Just Hosting Plan included',
            highlight: 'Just Hosting Plan',
          },
          '100/100 scores on Google PageSpeed Insights',
          'Best performance guaranteed',
          'Spam protection on every form',
          '24/7 support line',
          'Continuous improvements as your business needs change',
        ],
        features: [
          'Strategy sessions and custom design matched to your brand',
          'Launch support covering domains, analytics, and integrations',
          'Managed hosting with monthly improvements and campaign support',
        ],
        cta: 'Start your project',
      },
      {
        name: 'Just Hosting',
        description: 'Bring us your existing site and we will host, monitor, and update it — no setup fee, just a monthly plan.',
        price: 'Monthly subscription only',
        assurances: [
          'Free domain name included',
          'Free SSL certificate included',
          'Professional emails included',
          '100% uptime guaranteed',
          '7 backups a week included',
          'All previous versions of the site retained',
          'Regular security updates',
        ],
        features: [
          'Free migration, DNS setup, and launch checks',
          'High-speed hosting with backups and security monitoring',
          'Monthly content tweaks plus performance and security updates',
        ],
        cta: 'Start your project',
      },
    ],
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        question: 'How quickly can we start?',
        answer:
          'We typically kick off within two business days once the scope is confirmed. Tight timelines are doable — let us know and we will propose an accelerated plan.',
      },
      {
        question: 'Can we upgrade to another plan later?',
        answer:
          'Yes. Many clients begin with a build or hosting engagement and later expand into the full lifecycle partnership when they are ready for ongoing collaboration.',
      },
      {
        question: 'Do you support multiple languages and integrations?',
        answer:
          'Absolutely. Multi-language, analytics, and third-party integrations are part of every plan. We tailor the stack to your tools so everything works together on day one.',
      },
    ],
    footer: 'Need something bespoke? Email contact@everbranch.co and we will tailor a plan that fits.',
  },
  contact: {
    heroHeading: 'Say hello to the team',
    heroSubheading: 'Reach out for private dining, events, or general enquiries.',
    info: {
      heading: 'Visit us',
      description: 'A beloved jazz restaurant in the heart of Sofia blending refined dining with live performances since 2006.',
      addressHeading: 'Our Address',
      phoneHeading: 'Our Phone',
      mobileLabel: 'Mobile',
      landlineLabel: 'Stationary',
      emailHeading: 'Our Email',
      directions: 'Get directions',
      callUs: 'Call us',
      emailUs: 'Send a message',
      mapTitle: 'Map to CheckPoint Charly',
      viewLargeMap: 'View larger map',
    },
    fields: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      subject: 'Subject',
      message: 'Your message',
    },
    placeholders: {
      name: 'Ivan Ivanov',
      email: 'guest@example.com',
      phone: '+359 886 577 342',
      message: 'Let us know when you would like to visit and any special requests.',
      subject: 'How can we help?',
      emailAddress: 'Your email address',
    },
    validation: {
      name: 'Please enter at least two characters.',
      email: 'Please enter a valid email address.',
      phone: 'Please enter a valid phone number.',
      subject: 'Please choose a subject.',
      message: 'Please share a brief message.',
      captcha: 'Please confirm you are not a robot.',
    },
    submission: {
      success: 'Thank you! We will be in touch very soon.',
      error: 'Something went wrong. Please try again or call us directly.',
      sending: 'Sending…',
      cta: 'Send message',
      captchaMissing: 'Our spam protection is temporarily unavailable. Please call or email us directly.',
      subjectTemplate: 'New enquiry from {name}',
      notProvided: 'Not provided',
    },
    floatingCta: 'Make a booking',
    backToTop: 'Back to top',
  },
  infinityLoop: {
    badge: 'Launch loop',
    heading: 'How we launch and refine every site',
    body:
      'From kickoff to post-launch reporting, our team guides you through a fast, automated delivery cycle that keeps every site on schedule and on brand.',
    steps: [
      {
        label: 'Discover',
        tagline: 'Kickoff call to capture site goals, audiences, and success metrics.',
        highlights: ['Kickoff', 'goals', 'audiences', 'success'],
        position: { top: '18%', left: '27%' },
      },
      {
        label: 'Plan',
        tagline: 'Timeline, pages, and integrations mapped within the first 24 hours.',
        highlights: ['Timeline', 'integrations', '24 hours'],
        position: { top: '50%', left: '8%' },
      },
      {
        label: 'Create',
        tagline: 'Design and copy tailored for customers, readers, and partners.',
        highlights: ['Design', 'customers', 'partners'],
        position: { top: '82%', left: '27%' },
      },
      {
        label: 'Automate',
        tagline: 'Automated hosting, backups, and workflows keep updates instant.',
        highlights: ['Automated', 'hosting', 'workflows'],
        position: { top: '70%', left: '50%' },
      },
      {
        label: 'Launch',
        tagline: 'Domains, SSL, and email go live with our team on standby.',
        highlights: ['Domains', 'SSL', 'email'],
        position: { top: '82%', left: '73%' },
      },
      {
        label: 'Track',
        tagline: 'Live dashboards show signups, sales, and engagement.',
        highlights: ['dashboards', 'signups', 'sales', 'engagement'],
        position: { top: '50%', left: '93%' },
      },
      {
        label: 'Optimize',
        tagline: 'Performance tuned with A/B-ready modules and SEO hygiene.',
        highlights: ['Performance', 'A/B-ready', 'SEO'],
        position: { top: '18%', left: '73%' },
      },
      {
        label: 'Evolve',
        tagline: 'Post-launch insights shape the roadmap for your next update.',
        highlights: ['Post-launch', 'insights', 'next update'],
        position: { top: '32%', left: '50%' },
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    intro: 'CheckPoint Charly values the privacy of every guest and visitor. This policy explains how we collect, use, and protect personal data shared online, by email, or in person at the restaurant.',
    sections: [
      {
        title: 'Who we are',
        paragraphs: [
          'Checkpoint Charly LTD ("Checkpoint Charly", "we", "us") operates a restaurant located at 12 Ivan Vazov Street, Sofia 1000, Bulgaria. When you contact us, make a reservation, or visit our website, we act as the data controller for the personal information that you provide.',
          'You can reach our privacy contact by email at check_charly@abv.bg or by post at our restaurant address noted above.',
        ],
      },
      {
        title: 'Personal data we collect',
        paragraphs: ['We only collect the information needed to run the restaurant and respond to guest requests. This includes:'],
        listTitle: 'Categories of data',
        bullets: [
          'Contact details such as your name, email address, and telephone number when you complete the contact form or call us.',
          'Reservation details including preferred date, time, party size, and special requests shared by phone or online.',
          'Feedback or enquiry information from emails or messages you send to us, including any attachments you choose to include.',
          'Basic website analytics (for example page views and referral sources) collected via privacy-friendly tools without storing full IP addresses.',
          'Newsletter subscriptions when you opt in, along with your communication preferences.',
        ],
      },
      {
        title: 'How we use personal data',
        paragraphs: ['Personal data is processed for clearly defined purposes that are necessary to operate the restaurant and communicate with guests.'],
        listTitle: 'Primary purposes',
        bullets: [
          'Managing reservations and confirming availability by phone or email.',
          'Answering questions about menus, dietary requirements, private dining, or events.',
          'Sending service updates or promotions when you have opted into our newsletter (you may unsubscribe at any time).',
          'Improving our website and restaurant services through aggregated analytics.',
          'Complying with legal obligations under Bulgarian law, such as tax bookkeeping or responding to lawful requests.',
        ],
      },
      {
        title: 'Legal bases for processing',
        paragraphs: ['We rely on the following legal bases under the GDPR:'],
        bullets: [
          'Performance of a contract to confirm reservations and deliver dining services you request.',
          'Legitimate interests to maintain business records, respond to enquiries, and improve our guest experience.',
          'Consent when you opt into marketing communications or provide optional preference information.',
          'Compliance with legal obligations for accounting, safety, and public health requirements.',
        ],
      },
      {
        title: 'Retention',
        paragraphs: [
          'We keep personal data only for as long as necessary for the purposes above. Typical retention periods include:',
        ],
        bullets: [
          'Reservation and enquiry correspondence: up to 12 months after your visit, unless a longer period is legally required.',
          'Newsletter subscription data: until you unsubscribe or we close the mailing list.',
          'Accounting and tax records: up to 10 years in line with Bulgarian statutory requirements.',
        ],
      },
      {
        title: 'Processors and sharing',
        paragraphs: [
          'We do not sell personal data. We may share limited information with trusted service providers such as email delivery platforms or website hosting partners who support our operations. Each provider is bound by contracts that require confidentiality and GDPR-compliant safeguards.',
          'If required by law, we may disclose information to public authorities such as the National Revenue Agency or health inspectors.',
        ],
      },
      {
        title: 'Data subject rights',
        paragraphs: ['Guests located in the European Union or United Kingdom have the following rights regarding their personal data:'],
        listTitle: 'Your rights',
        bullets: [
          'Access – request confirmation and a copy of the personal data we hold.',
          'Rectification – ask us to correct incomplete or inaccurate details.',
          'Erasure – request deletion where data is no longer needed or you withdraw consent.',
          'Restriction – ask us to pause processing while a request is reviewed.',
          'Objection – object to processing based on legitimate interests or direct marketing.',
          'Portability – receive a copy of the data you provided in a portable format.',
          'Complaint – contact the Bulgarian Commission for Personal Data Protection if you believe we have processed your data unlawfully.',
        ],
      },
      {
        title: 'Security measures',
        paragraphs: [
          'We use secure email providers, access controls, and regularly updated software to protect personal data. Staff are trained to handle guest information responsibly and only authorised team members can view reservation or enquiry details.',
        ],
      },
      {
        title: 'Cookies and analytics',
        paragraphs: [
          'Our website uses essential cookies to operate and privacy-friendly analytics to understand overall traffic. You can control cookies through your browser settings at any time.',
        ],
      },
      {
        title: 'Children',
        paragraphs: [
          'Our services are aimed at adults planning restaurant visits. We do not knowingly collect personal data from children under 16. If you believe a child has provided us with personal data, please contact us so we can delete it.',
        ],
      },
      {
        title: 'Contact and requests',
        paragraphs: [
          'To exercise your rights or ask a privacy question, email check_charly@abv.bg or speak with our team at the restaurant. We respond within 30 days and may ask for proof of identity before fulfilling certain requests.',
        ],
      },
      {
        title: 'Updates to this policy',
        paragraphs: [
          'We review this notice periodically. Updates will be posted on our website and dated so you can see when changes take effect.',
        ],
      },
    ],
    footer: {
      lastUpdated: 'Last updated: October 31, 2025',
      requestText: 'For privacy requests, email check_charly@abv.bg or visit us at 12 Ivan Vazov Street, Sofia.',
    },
  },
  terms: {
    title: 'Terms & Conditions',
    intro:
      'These terms explain how to use the CheckPoint Charly website, submit reservation enquiries, and interact with our team. By visiting our site or contacting us, you agree to the practices described below.',
    clauses: [
      {
        title: 'Restaurant details',
        paragraphs: [
          'CheckPoint Charly is operated by Checkpoint Charly LTD, registered in Bulgaria with address 12 Ivan Vazov Street, Sofia 1000. Our official email is check_charly@abv.bg and our contact phone numbers are listed on the Contact page.',
        ],
      },
      {
        title: 'Use of the website',
        paragraphs: [
          'The content on this website is provided for general information about the restaurant. We aim to ensure accuracy but menu items, prices, and promotions can change without notice. Reproduction of site content requires prior written consent from CheckPoint Charly.',
        ],
      },
      {
        title: 'Reservation enquiries',
        paragraphs: [
          'Forms submitted through the website collect details needed to prepare your visit. Submitting a form does not guarantee a booking. A member of our team will confirm availability by phone or email. Please inform us promptly if you need to change or cancel a reservation.',
        ],
      },
      {
        title: 'Guest responsibilities',
        paragraphs: [
          'Guests agree to provide accurate contact details, respect reservation times, and adhere to house rules, including any dress code or safety guidance. We reserve the right to refuse service if behaviour endangers staff or other guests.',
        ],
      },
      {
        title: 'Allergies and dietary requirements',
        paragraphs: [
          'Please inform us of allergies or dietary requirements in advance. We take reasonable care to avoid cross-contamination but cannot guarantee that dishes are free from all allergens.',
        ],
      },
      {
        title: 'Liability',
        paragraphs: [
          'We strive to keep the website available and accurate, but we cannot guarantee uninterrupted access. CheckPoint Charly is not liable for indirect or consequential losses arising from the use of the website or delays in confirming reservations, except where liability cannot be excluded under Bulgarian law.',
        ],
      },
      {
        title: 'Intellectual property',
        paragraphs: [
          'All logos, photography, menu descriptions, and written content belong to CheckPoint Charly unless otherwise noted. You may not reuse or modify this material without permission.',
        ],
      },
      {
        title: 'Third-party links',
        paragraphs: [
          'Our website may include links to third-party services such as social media platforms or delivery partners. We are not responsible for the content or privacy practices of those external sites.',
        ],
      },
      {
        title: 'Changes to these terms',
        paragraphs: [
          'We may update these terms to reflect operational or legal changes. When updates occur, the revised version will be posted with a new effective date.',
        ],
      },
    ],
    footer: {
      effectiveDate: 'Effective date: October 31, 2025',
      contactIntro: 'For questions about these terms, contact us at',
      contactEmail: 'check_charly@abv.bg',
      contactDetails: 'or by post at 12 Ivan Vazov Street, Sofia 1000, Bulgaria.',
    },
  },
}
